import React from 'react';
import { observer } from 'mobx-react-lite';

import { CoverPlaceholder } from '../../strip-board/components/cover-placeholder';
import { AddStripMenu } from './add-strip-menu';
import { Stripboard } from '../../../app/entities/stripboard';
import { CollapsibleItem } from '../../../components/collapsible-item/collapsible-item';
import { timeParser } from '../../../lib/utils/time-parser';
import { getRenderedTime } from '../../../lib/utils/Convertor';
import {
  CellContent,
  CellLabel,
  HorizontalCell,
  VerticalCell,
} from '../../shootingdays/components/styled-mobile-table';
import { CoverItem, Image, HiddenCoverOverlay } from './styled-strips';
import { Shot } from '../../../app/entities/shot';

import type { ShotStrip } from '../../../app/entities/strip';
import type { Frame } from '../../../lib/utils/transform-image';
import type { Property } from '../../../app/entities/property';
import { PropertyType } from '@producer/properties';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { minutesFormatter } from '../../../lib/time-formatters';

import styles from './shot-strip.mobile.module.scss';
type MobileShotStripProps = {
  strip: ShotStrip;
  stripboard: Stripboard;
  properties: Property[];
};

export const MobileShotStripComponent: React.FC<MobileShotStripProps> = observer(
  ({ stripboard, strip, properties }) => {
    const shot = Shot.getOne(strip.data.shotId);
    const locations = shot?.project?.locations;
    const coverProp = properties.find((property) => property.key === 'cover');

    const renderFixedProperties = (key: Property['key']) => {
      if (!shot) return;

      const targetProp = properties.find((property) => property.key === key);
      if (!targetProp) return;

      const value = shot.getProperty(targetProp._id);

      switch (key) {
        case 'cover':
          return (
            <CoverItem className={styles['cover-item']}>
              {shot?.cover?.src ? (
                <Image src={shot?.cover?.src || ''} />
              ) : (
                <CoverPlaceholder onUpload={(file) => handleEdit({ file })} />
              )}

              <HiddenCoverOverlay data-hide={!strip.data.isHidden} />
            </CoverItem>
          );
        case 'title':
          return value && <h3 className={styles['shot-title']}>{value}</h3>;
        case 'order':
          return (
            <HeaderData>
              <CellLabel>{targetProp.name}</CellLabel>

              <CellContent className={styles['cell-content']}>{value}</CellContent>
            </HeaderData>
          );
        case 'startTime':
          return (
            <HeaderData>
              <CellLabel>{targetProp.name}</CellLabel>

              <CellContent className={styles['cell-content']}>
                {getRenderedTime(strip.startTime)}
              </CellContent>
            </HeaderData>
          );
        case 'estimatedTime':
          return (
            !strip.data.isHidden && (
              <HeaderData>
                <CellLabel>{targetProp.name}</CellLabel>

                <CellContent className={styles['cell-content']}>
                  {minutesFormatter(value || 0)}
                </CellContent>
              </HeaderData>
            )
          );

        default:
          return null;
      }
    };

    const renderProperty = (property: Property) => {
      if (!shot) return;

      if (['cover', 'title', 'order', 'startTime', 'estimatedTime'].includes(property.key)) {
        return;
      }

      const value = shot.getProperty(property._id);

      const multiValue = Array.isArray(value) ? value.join(', ') : value;

      if (['timing', 'setting'].includes(property.key)) {
        return (
          <HorizontalCell>
            <CellLabel>{property.name}</CellLabel>

            <CellContent className={styles['cell-content']} style={{ textTransform: 'capitalize' }}>
              {value}
            </CellContent>
          </HorizontalCell>
        );
      }

      switch (property.type) {
        case PropertyType.SingleSelect:
          if (property.resourceType === 'Location') {
            const location = locations?.find((location) => location._id === shot?.locationId);

            return (
              <HorizontalCell>
                <CellLabel>{property.name}</CellLabel>

                <CellContent className={styles['cell-content']}>{location?.name}</CellContent>
              </HorizontalCell>
            );
          }

          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent className={styles['cell-content']}>{value}</CellContent>
            </VerticalCell>
          );
        case PropertyType.MultiSelect:
          if (property.resourceType === 'Character') {
            const characters = shot?.characters?.map((character) => character.name).join(', ');

            return (
              <VerticalCell key={property._id}>
                <CellLabel>{property.name}</CellLabel>

                {characters && (
                  <CellContent className={styles['cell-content']}>{characters}</CellContent>
                )}
              </VerticalCell>
            );
          } else if (property.resourceType === 'Tag') {
            return (
              <VerticalCell>
                <CellLabel>{property.name}</CellLabel>

                <TagsViewer
                  key={property._id}
                  targetId={shot._id}
                  targetType="shot"
                  overflow="wrap"
                  alignment="start"
                />
              </VerticalCell>
            );
          }

          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent className={styles['cell-content']}>{multiValue}</CellContent>
            </VerticalCell>
          );
        case PropertyType.LongText:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent
                className={styles['cell-content']}
                dangerouslySetInnerHTML={{
                  __html: value,
                }}
              />
            </VerticalCell>
          );
        case PropertyType.SmallText:
        case PropertyType.Number:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent className={styles['cell-content']}>{value}</CellContent>
            </VerticalCell>
          );
        case PropertyType.Duration:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent className={styles['cell-content']}>
                {timeParser(value || 0).toString()}
              </CellContent>
            </VerticalCell>
          );

        //TODO: add the rest of the property types
        default:
          return;
      }
    };

    const handleEdit = async (data: Partial<Shot> & { file?: Frame }) => {
      if (!shot) {
        return;
      }

      shot.update(data);
    };

    if (!shot) {
      return;
    }

    return (
      <div className={styles['container']}>
        <CollapsibleItem
          title={
            <div className={styles['header']}>
              {renderFixedProperties('cover')}

              <div className={styles['header-data-wrapper']} data-hidden={strip.data.isHidden}>
                {!coverProp && (
                  <h3 className={styles['shot-title']} data-placeholder="true">
                    Expand to see more shot properties
                  </h3>
                )}

                {renderFixedProperties('title')}
                {renderFixedProperties('order')}
                {renderFixedProperties('startTime')}
                {renderFixedProperties('estimatedTime')}
              </div>
            </div>
          }
          isTransparent
        >
          <div className={styles['content-container']} data-hidden={!!strip.data.isHidden}>
            {properties.map((property) => (
              <React.Fragment key={property._id}>{renderProperty(property)}</React.Fragment>
            ))}

            <HorizontalCell>
              <CellLabel>Actions</CellLabel>

              <AddStripMenu
                stripId={strip._id}
                projectId={stripboard.projectId}
                position={strip.position}
                shot={shot}
                stripType="shot"
              />
            </HorizontalCell>
          </div>
        </CollapsibleItem>
      </div>
    );
  },
);

/** Exported and rendered by break-strip.mobile. */
export const HeaderData = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <HorizontalCell
    className={className ? `${styles['header-data']} ${className}` : styles['header-data']}
    {...props}
  />
);
